import React from 'react';
import { Box, Card, CardActionArea, CardContent, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';
import type { ServicePreset } from './types';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors } from '../../theme/opsTokens';

interface ServicePresetStepProps {
  selectedPreset: ServicePreset | null;
  onSelect: (preset: ServicePreset) => void;
}

const presetCards: Array<{
  id: ServicePreset;
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
}> = [
  {
    id: 'on-demand',
    titleKey: 'preset.onDemand.title',
    descriptionKey: 'preset.onDemand.description',
    icon: <Icon d={ICON_FALLBACKS.Commute} size={20} />,
  },
  {
    id: 'school',
    titleKey: 'preset.school.title',
    descriptionKey: 'preset.school.description',
    icon: <Icon d={ICON_FALLBACKS.School} size={20} />,
  },
  {
    id: 'paratransit',
    titleKey: 'preset.paratransit.title',
    descriptionKey: 'preset.paratransit.description',
    icon: <Icon name="wheelchair-move" size={20} />,
  },
];

export default function ServicePresetStep({ selectedPreset, onSelect }: ServicePresetStepProps) {
  const { t } = useTranslation('onboarding');
  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" sx={{ mb: 1.5 }}>
        {t('preset.title')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('preset.description')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
        {presetCards.map((card) => {
          const selected = selectedPreset === card.id;
          const title = t(card.titleKey);
          return (
            <Card
              key={card.id}
              variant="outlined"
              sx={(theme) => ({
                borderColor: selected ? opsColors.brand.primary : opsColors.neutral.line,
                backgroundColor: selected ? opsColors.neutral.panelAlt : opsColors.neutral.panel,
              })}
            >
                <CardActionArea
                  onClick={() => onSelect(card.id)}
                  aria-label={t('preset.selectAria', { title })}
                  data-testid={`wizard.setup.preset-card.${card.id}`}
                >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={(theme) => ({
                        width: 28,
                        height: 28,
                        borderRadius: 1.25,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: selected ? opsColors.brand.primary : opsColors.neutral.panelAlt,
                        color: selected ? '#ffffff' : opsColors.neutral.inkSoft,
                      })}
                    >
                      {card.icon}
                    </Box>
                    <Typography variant="h6">{title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t(card.descriptionKey)}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
